// @ts-nocheck
import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetBody, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Layers } from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';
import { toast } from 'sonner';
import { ProductNodeSelector } from './product-node-selector';

interface LinkProductAreasDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityId: string;
  entityType: 'idea' | 'feedback';
  onSuccess?: () => void;
}

export function LinkProductAreasDialog({ 
  open, 
  onOpenChange, 
  entityId,
  entityType,
  onSuccess 
}: LinkProductAreasDialogProps) {
  const { workspace, membership } = useWorkspace();
  const [selectedProductNodes, setSelectedProductNodes] = useState<string[]>([]);
  const [existingNodes, setExistingNodes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const tableName = entityType === 'idea' ? 'idea_product_mapping' : 'feedback_product_mapping';
  const idField = entityType === 'idea' ? 'idea_id' : 'feedback_id';

  useEffect(() => {
    if (open && workspace && entityId) {
      loadExistingLinks();
    }
  }, [open, workspace, entityId]);

  const loadExistingLinks = async () => {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('product_item_id, product_id')
        .eq(idField, entityId);

      if (error) throw error;

      // Collect both structure item IDs and product IDs
      const nodeIds = data?.map(item => item.product_item_id || item.product_id).filter(Boolean) || [];
      setExistingNodes(nodeIds);
      setSelectedProductNodes(nodeIds);
    } catch (error: any) {
      console.error('Error loading existing product links:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Separate product IDs from structure item IDs
      const { productIds, structureItemIds } = await separateProductAndStructureIds(selectedProductNodes);

      // Combine both for comparison
      const allNewIds = [...productIds, ...structureItemIds];

      // Calculate links to add and remove
      const toAdd = allNewIds.filter(id => !existingNodes.includes(id));
      const toRemove = existingNodes.filter(id => !allNewIds.includes(id));

      // Remove unselected links
      if (toRemove.length > 0) {
        // Need to remove by checking both product_item_id and product_id
        for (const id of toRemove) {
          const { error: deleteError } = await supabase
            .from(tableName)
            .delete()
            .eq(idField, entityId)
            .or(`product_item_id.eq.${id},product_id.eq.${id}`);

          if (deleteError) throw deleteError;
        }
      }

      // Add new links
      if (toAdd.length > 0) {
        // Check which IDs are products vs structure items
        const { productIds: newProductIds, structureItemIds: newStructureIds } = await separateProductAndStructureIds(toAdd);
        
        const linksToInsert = [
          ...newProductIds.map(id => ({
            [idField]: entityId,
            product_id: id,
            product_item_id: null
          })),
          ...newStructureIds.map(id => ({
            [idField]: entityId,
            product_item_id: id,
            product_id: null
          }))
        ];

        const linksWithMetadata = linksToInsert.map(link => ({
          ...link,
          mapped_by: membership?.user_id,
        }));

        const { error: insertError } = await supabase
          .from(tableName)
          .insert(linksWithMetadata);

        if (insertError) throw insertError;
      }

      toast.success('Product areas updated successfully', {
        description: `Added ${toAdd.length}, removed ${toRemove.length}`
      });

      onSuccess?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating product links:', error);
      toast.error('Failed to update product areas', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Separate node IDs into product IDs and structure item IDs
  const separateProductAndStructureIds = async (nodeIds: string[]): Promise<{
    productIds: string[];
    structureItemIds: string[];
  }> => {
    if (nodeIds.length === 0) return { productIds: [], structureItemIds: [] };
    
    // Check which IDs exist in product_structure_items
    const { data: structureItems, error: structureError } = await supabase
      .from('product_structure_items')
      .select('id')
      .in('id', nodeIds);
    
    if (structureError) {
      console.error('Error filtering structure items:', structureError);
      return { productIds: [], structureItemIds: [] };
    }
    
    const structureItemIds = structureItems?.map(item => item.id) || [];
    
    // IDs that aren't structure items must be products
    const productIds = nodeIds.filter(id => !structureItemIds.includes(id));
    
    return { productIds, structureItemIds };
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center space-x-2">
            <Layers className="h-5 w-5" />
            <span>Link Product Areas</span>
          </SheetTitle>
          <SheetDescription>
            Select which product areas this {entityType} relates to
          </SheetDescription>
        </SheetHeader>

        <SheetBody>
          <ProductNodeSelector
            selectedNodeIds={selectedProductNodes}
            onSelectionChange={setSelectedProductNodes}
          />
        </SheetBody>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : `Save Changes`}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}